import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { inspectionTypesService } from "../services/inspectionTypesService";
import styles from "../assets/inspectionTypeEdit.module.css";

const InspectionTypeEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    color: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const dataLoadedRef = useRef(false); // Track if data has been loaded to prevent resets

  // Predefined colors for quick selection
  const predefinedColors = [
    "#ff0000",
    "#00ff00",
    "#0000ff",
    "#ffff00",
    "#ff00ff",
    "#00ffff",
    "#ffa500",
    "#800080",
    "#008000",
    "#ffc0cb",
    "#a52a2a",
    "#808080",
    "#000000",
    "#ffffff",
    "#5bc0de",
    "#5cb85c",
    "#d9534f",
    "#f0ad4e",
    "#337ab7",
    "#6f42c1",
    "#20c997",
    "#fd7e14",
  ];

  // Load inspection type data on component mount
  useEffect(() => {
    // Reset data loaded flag when ID changes
    dataLoadedRef.current = false;
    loadInspectionType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load inspection type data
  const loadInspectionType = async () => {
    // Prevent reloading if data has already been loaded and form has content
    // This prevents the form from being reset when user is editing
    if (dataLoadedRef.current && formData.name && formData.name !== "") {
      console.log("Data already loaded and form has content, skipping reload to preserve user input");
      return;
    }
    
    setInitialLoading(true);
    try {
      const response = await inspectionTypesService.getInspectionType(id);
      console.log("Full API response:", response);
      console.log("Response data:", response.data);
      console.log("Response status:", response.status);

      // Handle different response structures
      let inspectionData = null;

      if (response.data) {
        // Check if data is nested
        if (response.data.data) {
          inspectionData = response.data.data;
        } else if (response.data.name) {
          inspectionData = response.data;
        } else if (
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          inspectionData = response.data[0];
        }
      }

      console.log("Extracted inspection data:", inspectionData);

      if (inspectionData && (inspectionData.name || inspectionData.id)) {
        // Convert color name to hex code if needed
        let colorValue = inspectionData.color || "";
        if (colorValue && !colorValue.startsWith('#')) {
          const hexColor = convertColorNameToHex(colorValue);
          if (hexColor) {
            colorValue = hexColor;
            console.log(`Converted color name "${inspectionData.color}" to hex code "${colorValue}"`);
          } else {
            // If not a recognized color name, try adding # prefix
            colorValue = '#' + colorValue;
            if (!isValidHexColor(colorValue)) {
              colorValue = ""; // Invalid, clear it
              console.warn(`Invalid color value "${inspectionData.color}", cleared`);
            }
          }
        }
        
        const formDataToSet = {
          name: inspectionData.name || "",
          color: colorValue,
        };
        console.log("Setting form data from API:", formDataToSet);
        // Always set form data on initial load, but check if already loaded
        if (!dataLoadedRef.current) {
          setFormData(formDataToSet);
          dataLoadedRef.current = true;
          console.log("✅ Form data loaded and marked as loaded");
        } else {
          // Data already loaded, preserve existing form data (user might be editing)
          console.log("Data already loaded, preserving existing form data:", formData);
        }
      } else {
        console.error("No valid inspection data found in API response");
        toast.error("Inspection type not found");
        navigate("/inspection-types");
      }
    } catch (apiError) {
      console.error("API call failed:", apiError);
      toast.error("Failed to load inspection type. Please try again.");
      navigate("/inspection-types");
    } finally {
      setInitialLoading(false);
    }
  };


  // Debug formData changes
  useEffect(() => {
    console.log("Form data updated:", formData);
  }, [formData]);

  // Ensure form data is properly initialized
  useEffect(() => {
    console.log("Component mounted with ID:", id);
    console.log("Current form data:", formData);
    console.log("Initial loading:", initialLoading);
  }, [id, formData, initialLoading]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker) {
        const colorPickerElement = document.querySelector(`.${styles.colorPickerDropdown}`);
        const colorInputElement = document.querySelector(`.${styles.colorInput}`);
        const colorAddonElement = document.querySelector(`.${styles.inputGroupAddon}`);
        
        if (colorPickerElement && 
            !colorPickerElement.contains(event.target) &&
            !colorInputElement?.contains(event.target) &&
            !colorAddonElement?.contains(event.target)) {
          setShowColorPicker(false);
        }
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker, styles]);

  // Validate hex color code
  const isValidHexColor = (color) => {
    if (!color) return true; // Empty is valid (optional field)
    // Must start with # and be followed by 3 or 6 hex digits
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  // Convert color name to hex code (if it's a common color name)
  const convertColorNameToHex = (colorName) => {
    const colorMap = {
      'red': '#ff0000',
      'green': '#00ff00',
      'blue': '#0000ff',
      'yellow': '#ffff00',
      'orange': '#ffa500',
      'purple': '#800080',
      'pink': '#ffc0cb',
      'cyan': '#00ffff',
      'black': '#000000',
      'white': '#ffffff',
      'grey': '#808080',
      'gray': '#808080'
    };
    return colorMap[colorName.toLowerCase()] || null;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for color field - only accept hex codes
    if (name === 'color') {
      let processedValue = value.trim();
      
      // If it's a color name, convert to hex
      if (processedValue && !processedValue.startsWith('#')) {
        const hexColor = convertColorNameToHex(processedValue);
        if (hexColor) {
          processedValue = hexColor;
        } else {
          // If not a recognized color name and doesn't start with #, add #
          if (processedValue && !processedValue.startsWith('#')) {
            processedValue = '#' + processedValue;
          }
        }
      }
      
      // Validate hex format in real-time
      if (processedValue && !isValidHexColor(processedValue)) {
        // Show error but still allow typing
        setErrors((prev) => ({
          ...prev,
          color: "Please enter a valid hex color code (e.g., #ff0000)",
        }));
      } else {
        // Clear error if valid
        setErrors((prev) => ({
          ...prev,
          color: "",
        }));
      }
      
      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  // Handle color selection
  const handleColorSelect = (color) => {
    setFormData((prev) => ({
      ...prev,
      color: color,
    }));
    setShowColorPicker(false);

    if (errors.color) {
      setErrors((prev) => ({
        ...prev,
        color: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 255) {
      newErrors.name = "Name must not exceed 255 characters";
    }

    // Color validation - must be hex code format (not color names)
    if (formData.color && formData.color.trim() !== "") {
      if (!isValidHexColor(formData.color)) {
        newErrors.color = "Please enter a valid hex color code (e.g., #ff0000). Color names like 'red' or 'green' are not allowed.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await inspectionTypesService.updateInspectionType(id, formData);
      toast.success("Inspection Type updated successfully!");

      // Redirect to index page
      navigate("/inspection-types");
    } catch (error) {
      console.error("Error updating inspection type:", error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error("Failed to update inspection type. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/inspection-types");
  };

  // Show loading state while fetching data
  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading inspection type...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/inspection-types">Inspection Types</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Edit</span>
      </div>


      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Edit Inspection Type</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  {/* Name Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.name ? styles.hasError : ""
                        }`}
                        required
                        placeholder="Enter inspection type name"
                        disabled={initialLoading}
                      />
                      {errors.name && (
                        <div className={styles.errorMessage}>{errors.name}</div>
                      )}
                    </div>
                  </div>

                  {/* Color Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3}`}
                    >
                      Color
                    </label>
                    <div className={styles.colMd5} style={{ position: 'relative' }}>
                      <div className={styles.inputGroup}>
                        <input
                          type="text"
                          name="color"
                          value={formData.color || ""}
                          onChange={handleInputChange}
                          onBlur={(e) => {
                            // Validate on blur - convert to hex if needed
                            const value = e.target.value.trim();
                            if (value && !isValidHexColor(value)) {
                              const hexColor = convertColorNameToHex(value);
                              if (hexColor) {
                                setFormData((prev) => ({
                                  ...prev,
                                  color: hexColor,
                                }));
                                setErrors((prev) => ({
                                  ...prev,
                                  color: "",
                                }));
                              } else if (!value.startsWith('#')) {
                                // Try adding # prefix
                                const withHash = '#' + value;
                                if (isValidHexColor(withHash)) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    color: withHash,
                                  }));
                                  setErrors((prev) => ({
                                    ...prev,
                                    color: "",
                                  }));
                                }
                              }
                            }
                          }}
                          className={`${styles.formControl} ${
                            styles.colorInput
                          } ${errors.color ? styles.hasError : ""}`}
                          placeholder="#000000"
                          pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                          title="Enter a hex color code (e.g., #ff0000)"
                          disabled={initialLoading}
                        />
                        <span
                          className={styles.inputGroupAddon}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowColorPicker(!showColorPicker);
                          }}
                          style={{ cursor: "pointer" }}
                          title="Click to open color picker"
                        >
                          <i
                            className={styles.colorPreview}
                            style={{
                              backgroundColor: isValidHexColor(formData.color) 
                                ? (formData.color || "#ccc") 
                                : "#ccc",
                            }}
                          ></i>
                        </span>
                      </div>

                      {/* Color Picker Dropdown */}
                      {showColorPicker && (
                        <div 
                          className={styles.colorPickerDropdown}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.colorPickerHeader}>
                            <span>Select a color:</span>
                            <button
                              type="button"
                              className={styles.closeButton}
                              onClick={() => setShowColorPicker(false)}
                            >
                              ×
                            </button>
                          </div>
                          <div className={styles.colorGrid}>
                            {predefinedColors.map((color, index) => (
                              <div
                                key={index}
                                className={styles.colorOption}
                                style={{ backgroundColor: color }}
                                onClick={() => handleColorSelect(color)}
                                title={color}
                              ></div>
                            ))}
                          </div>
                          <div className={styles.colorPickerFooter}>
                            <input
                              type="text"
                              value={formData.color || ""}
                              onChange={handleInputChange}
                              name="color"
                              placeholder="Or enter hex color (e.g., #ff0000)"
                              className={styles.hexInput}
                              pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                              title="Enter a hex color code (e.g., #ff0000)"
                            />
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                              Only hex color codes are accepted (e.g., #ff0000)
                            </div>
                          </div>
                        </div>
                      )}

                      {errors.color && (
                        <div className={styles.errorMessage}>
                          {errors.color}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className={styles.formGroup}>
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm5}>
                      <button
                        style={{ backgroundColor: "#1B2046" }}
                        type="submit"
                        className={styles.btnHead}
                        disabled={loading}
                      >
                        <i className="glyphicon glyphicon-ok"></i>
                        {loading ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionTypeEdit;
